import {
  CSSProperties,
  ChangeEventHandler,
  DetailedHTMLProps,
  HTMLInputTypeAttribute,
  InputHTMLAttributes,
  MouseEventHandler,
  ReactElement,
} from 'react';
import { useDebouncedCallback } from '../../hooks/useDebouncedCallback';
import { classNames } from '../../utils';

interface InputProps
  extends Omit<
    DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    'className' | 'value' | 'onChange' | 'type'
  > {}

interface Props {
  title?: string;
  placeholder?: string;
  input?: InputProps;
  type?: HTMLInputTypeAttribute;
  value?: string;
  readOnly?: boolean;
  className?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  titlePosition?: 'default' | 'overlapping';
  validation?: {
    status?: 'error' | 'success';
    text?: string;
  };
  style?: CSSProperties;
  startIcon?: ReactElement;
  endIcon?: ReactElement;
  fullWidth?: boolean;
  debounce?: boolean;
  trailing?: ReactElement;
  disabled?: boolean;
  onClick?: MouseEventHandler<HTMLInputElement>;
  onFocus?: () => void;
  onBlur?: () => void;
}

export default function TextField({
  title,
  placeholder,
  input,
  type,
  value,
  readOnly,
  className,
  onChange,
  validation,
  style,
  startIcon,
  endIcon,
  fullWidth,
  titlePosition = 'default',
  onClick,
  debounce,
  trailing,
  disabled,
  onFocus,
  onBlur,
}: Props) {
  const debouncedOnChange = useDebouncedCallback(
    onChange ?? (() => {}),
    debounce ? 1000 : 0,
  );

  const titleElem = title ? (
    <label
      htmlFor="email"
      className={classNames(
        titlePosition === 'overlapping'
          ? 'absolute -top-2 left-2 z-10 inline-block bg-white px-1 text-xs font-medium text-gray-900'
          : 'block text-sm font-medium leading-6 text-gray-900',
      )}
    >
      {title}
    </label>
  ) : (
    <></>
  );

  return (
    <div className={fullWidth ? 'relative w-full' : 'relative'}>
      {title ? (
        <div>
          {titleElem}
          {trailing}
        </div>
      ) : (
        <></>
      )}
      <div className="relative">
        {startIcon ? (
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center ps-3">
            {startIcon}
          </div>
        ) : (
          <></>
        )}
        <input
          {...input}
          type={type}
          value={value}
          disabled={disabled}
          onChange={debouncedOnChange}
          className={classNames(
            'relative block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6',
            title && titlePosition !== 'overlapping' ? 'mt-2' : '',
            validation?.status === 'success' ? 'ring-green-300' : '',
            validation?.status === 'error' ? 'ring-red-300' : '',
            className,
            startIcon ? 'ps-10' : '',
            endIcon ? 'pe-10' : '',
          )}
          placeholder={placeholder}
          readOnly={readOnly}
          aria-invalid={validation?.status === 'error' ? 'true' : undefined}
          aria-describedby={validation?.text}
          style={style}
          onClick={onClick}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        {endIcon ? (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pe-3">
            {endIcon}
          </div>
        ) : (
          <></>
        )}
      </div>
      {validation?.text ? (
        <p
          className={classNames(
            'mt-2 text-sm',
            validation?.status === 'error' ? 'text-red-600' : '',
            validation?.status === 'success' ? 'text-green-600' : '',
          )}
          id="email-error"
        >
          {validation.text}
        </p>
      ) : (
        <></>
      )}
    </div>
  );
}
